import translations from '@translationKeys'

import { POneEvent } from '@modules/auth/hooks/types'
import SvgIcon from '@mui/material/SvgIcon'

export enum FILTER_LEVEL_TYPES {
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  MULTI_SELECT_LEVEL = 'MULTI_SELECT_LEVEL',
}

export type FacetItem = {
  name: string
  key: string
  type: FILTER_LEVEL_TYPES
  total: number
  checked?: boolean
  values: { name: string; total: number; checked?: boolean; values?: { name: string; total: number; checked?: boolean }[] }[]
}

export type FiltersSetup = Record<string, { facets: FacetItem[]; total: number; enabled?: boolean }>

export enum SortOrder {
  'asc' = 'asc',
  'desc' = 'desc',
}

export enum SortType {
  'text' = 'text',
  'date' = 'date',
}

export type SearchApiResponse<T> = {
  result: T
  total: number
  facets: FacetItem[]
}

export interface NumbersRange {
  min: number
  max: number
}

export type MetaFiltersResponse = {
  eventsYears: NumbersRange
}
export interface DynamicField {
  name: string
  value: unknown
}
export interface MenuLink {
  path: string
  icon: typeof SvgIcon
  text: string
}

export enum ProfileType {
  company = 'company',
  delegate = 'delegate',
  biotech = 'biotech',
  medtech = 'medtech',
  technologies = 'technologies',
  services = 'services',
}

export type AssetsProfileType = ProfileType.biotech | ProfileType.technologies | ProfileType.services | ProfileType.medtech

export interface EntityProfile {
  id: number
  '@type': ProfileType
  updatedAt: string
  createdAt: string
  fields: Record<string, string | string[]>
  eventId: POneEvent['id']
  eventData: POneEvent
  profileConfig: unknown[]
  followedFieldsUpdatedAt?: string
  isPrivate?: boolean
}

export interface Delegate extends EntityProfile {
  id: number
  title: string
  firstName: string
  lastName: string
  companyId: number
  companyName: string
  middleName?: string
  addressLine1?: string
  addressLine2?: string
  email?: string
  secondaryEmail?: string
  jobTitle?: string
  department?: string
  country?: string
  state?: string
  city?: string
  zip?: string
  website?: string
  phone?: string
  mobilePhone?: string
  profilePicture?: string
  partneringObjectives?: string
  profileSummary?: string
  deals?: string
  attachment?: string
  expertiseArea?: string
  hasSharedInfo?: boolean
  sharedInfo?: {
    profilePhoneNumber?: string | null
    profileMobileNumber?: string | null
    profilePrimaryEmail?: string | null
    profileSecondaryEmail?: string | null
  }
}

export interface Company extends EntityProfile {
  name: string
  description: string
  hasAssets?: boolean
  assetLatestCreatedAt?: string
  isPublished?: boolean
  assetsSummary?: string
  profileData: {
    email: string
    phone?: string
    logo?: string
  }
  counters?: Record<AssetsProfileType, number>
  numberOfEmployees?: string
  sector?: string
  sectorUpdatedAt?: string
  subSectors?: string[]
  subSectorsUpdatedAt?: string
  primaryTherapeuticAreas?: string[]
  primaryTherapeuticAreasUpdatedAt?: string
  ownershipState?: string
  investmentStage?: string
  meetingPreferences?: string
  investmentTimeframe?: string
  investmentTimeframeUpdatedAt?: string

  addressLine1?: string
  addressLine2?: string
  country?: string
  state?: string
  city?: string
  zip?: string

  website?: string
  logo?: string

  founded?: string
  sourceOfFoundation?: string
  primaryTickerSymbol?: string
  primaryStockExchange?: string
  secondaryListings?: string
  ipo?: string
  ipoUpdatedAt?: string
  additionalListingInfo?: string
  phdNumber?: string
  partnerStrategy?: string
  clientPortfolio?: string
  corpHighlights?: string
  anticipatedCorpHighlights?: string
  attachment?: { url: string; customFileName: string }
  linkedin?: string
  twitter?: string
  facebook?: string
}

/** Split to separate types ? */
export interface CompanyAsset extends EntityProfile {
  id: number
  companyId: Company['id']
  companyName: string
  sectionId: string

  title: string
  description?: string

  differentiator?: string
  differentiatorUpdatedAt?: string
  orphanDiseaseIndication?: string
  devPhase?: string
  devPhaseUpdatedAt?: string
  primaryMarket?: string

  modeOfAdministration?: string
  moleculeType?: string
  marketingRightAvailableSought?: string[]

  categorySector?: string
  categorySectorUpdatedAt?: string
  categorySubSectors?: string[]
  categorySubSectorsUpdatedAt?: string

  serviceAvailability?: string[]
  serviceAvailabilityUpdatedAt?: string[]

  clinicalStrategy?: string
  clinicalStrategyUpdatedAt?: string
  keyPublications?: string
  ipRights?: string
  ipRightsUpdatedAt?: string
  partners?: string
  url?: string
  attachment?: { url: string; customFileName: string }
  fieldOfUseRestriction?: string

  duplicate_ids?: string[]
}

export interface AssetLatestCompany {
  createdAt?: string
  eventStartDate?: string
}

export interface HistoryProfileEvent extends POneEvent {
  profileCreatedAt: string
}

export interface GenericHistoryProfile<T, P> {
  id: number
  '@type': P
  latest: T
  latestCompany?: AssetLatestCompany
  events?: HistoryProfileEvent[]
  hasPublicProfile?: boolean
  isContactable?: boolean
  _isViewed?: boolean
  profiles?: T[]
}

export type DelegateHistoryProfile = GenericHistoryProfile<Delegate, ProfileType.delegate>

export type CompanyHistoryProfile = GenericHistoryProfile<Company, ProfileType.company>

export type CompanyAssetHistoryProfile = GenericHistoryProfile<CompanyAsset, AssetsProfileType>

export type AnyProfile = Delegate | Company | CompanyAsset

export type SearchProfilesResponse = AnyResultProfile[] | GroupedResultProfile[]

export type AnyResultProfile = DelegateHistoryProfile | CompanyHistoryProfile | CompanyAssetHistoryProfile
export type GroupedResultProfile = {
  company: CompanyHistoryProfile
  profiles: (DelegateHistoryProfile | CompanyAssetHistoryProfile)[]
}

/** Type guards */
export const isCompany = (p: AnyResultProfile): p is CompanyHistoryProfile => {
  return p['@type'] === ProfileType.company
}

export const isDelegate = (p: AnyResultProfile): p is DelegateHistoryProfile => {
  return p['@type'] === ProfileType.delegate
}

export const isGroupedByCompanyItem = (p: GroupedResultProfile | AnyResultProfile): p is GroupedResultProfile => {
  return !!(p as GroupedResultProfile)?.company
}

/** Split to separate types ? */
export const isCompanyAsset = (p: AnyResultProfile): p is CompanyAssetHistoryProfile => {
  return [ProfileType.services, ProfileType.biotech, ProfileType.technologies, ProfileType.medtech].includes(p['@type'])
}

export type SelectedFiltersMapByProfiles = Record<ProfileType, { field: string; value: string }[]>

export enum MetaFilterTypes {
  BY_NEW_ITEMS = 'BY_NEW_ITEMS',
  BY_DATE_RANGE = 'BY_DATE_RANGE',
  BY_EVENT = 'BY_EVENT',
  BY_RECENT = 'BY_RECENT',
  DEFAULT = 'DEFAULT',
}

export interface SelectedMetaFilters {
  dateFrom?: string
  dateTo?: string
  eventCode?: number
}

export interface UserEventsOptions {
  label: string
  value: number
  hidden?: boolean
}
export interface MetaFilterSelectedDate {
  from: number
  to: number
}

export interface MetaFilterSelectedData {
  type: MetaFilterTypes
  value: MetaFilterSelectedDate | number | string
}

export interface PredictiveSearchResponse {
  terms: string[]
  companies: string[]
}

export enum SearchFilterByRecent {
  SEARCH_FILTER_BY_RECENT_WEEK = 'SEARCH_FILTER_BY_RECENT_WEEK',
  SEARCH_FILTER_BY_RECENT_MONTH = 'SEARCH_FILTER_BY_RECENT_MONTH',
  SEARCH_FILTER_BY_RECENT_THREE_MONTHS = 'SEARCH_FILTER_BY_RECENT_THREE_MONTHS',
}

export enum HistoryFilterOptions {
  MY_MEETINGS = 1,
  PERSONAL_HISTORY = 2,
  COMPANY_MEETINGS = 3,
  NO_HISTORY = 4,
}

export const HISTORY_FILTER_OPTIONS_LABELS = {
  [HistoryFilterOptions.MY_MEETINGS]: translations.SEARCH_HISTORY_FILTER_MY_MEETINGS,
  [HistoryFilterOptions.PERSONAL_HISTORY]: translations.SEARCH_HISTORY_FILTER_PERSONAL,
  [HistoryFilterOptions.COMPANY_MEETINGS]: translations.SEARCH_HISTORY_FILTER_COMPANY_HISTORY,
  [HistoryFilterOptions.NO_HISTORY]: translations.SEARCH_HISTORY_FILTER_NO_HISTORY,
}

export const MAX_EVENTS_CHIPS_TO_SHOW = 3
